import { useState, useCallback, useMemo, useRef } from 'react';
import { SbNotification } from 'uiCore';
import { getCompRequisitionView, insertCompensation, updateCompensation } from 'Api/apiService';
import { COMP_SOURCE, UPDATE_CHANNEL, COMP_MESSAGES, COMP_ERROR_CODES } from 'constants/compensation.constants';

/**
 * Maps backend error_code to a notification.
 * Shows the raw backend message directly in the toaster.
 * Returns true if a known error code was handled, false otherwise.
 */
const handleCompErrorCode = (error) => {
  const errCode = error?.response?.data?.error_code;
  const backendMsg = error?.response?.data?.message || '';
  const status = error?.response?.status;

  // 401 — JWT expired / invalid
  if (status === 401 || errCode === COMP_ERROR_CODES.INVALID_JWT) {
    SbNotification.error(COMP_MESSAGES.ERR_SESSION_EXPIRED, backendMsg || COMP_MESSAGES.ERR_SESSION_EXPIRED_DESC);
    return true;
  }

  // Warning-level codes (show as warning)
  if (errCode === COMP_ERROR_CODES.VALUE_UNCHANGED || errCode === COMP_ERROR_CODES.NO_FIELDS_FOR_UPDATE) {
    SbNotification.warning(COMP_MESSAGES.SAVE_FAILED, backendMsg);
    return true;
  }

  // Error-level codes — show backend message directly
  if (
    errCode === COMP_ERROR_CODES.FX_RATE_NOT_CONFIGURED ||
    errCode === COMP_ERROR_CODES.INVALID_COMPENSATION ||
    errCode === COMP_ERROR_CODES.INVALID_POSITIONS ||
    errCode === COMP_ERROR_CODES.MISSING_FIELD ||
    errCode === COMP_ERROR_CODES.NOT_FOUND ||
    errCode === COMP_ERROR_CODES.COMP_EXISTS_OR_NOT_FOUND
  ) {
    SbNotification.error(COMP_MESSAGES.SAVE_FAILED, backendMsg);
    return true;
  }

  // 500-level — Server errors (7040, 7111)
  if (status >= 500 || errCode === COMP_ERROR_CODES.BQ_QUERY_FAILED || errCode === COMP_ERROR_CODES.INTERNAL_ERROR) {
    SbNotification.error(COMP_MESSAGES.ERR_SERVER, backendMsg || COMP_MESSAGES.ERR_SERVER_DESC);
    return true;
  }

  return false;
};

const useCompensationManagement = (email) => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Ant table filters (managed by Ant's onChange callback)
  const [tableFilters, setTableFilters] = useState({});

  // Inline edit state
  const [editingRowId, setEditingRowId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [savingRowId, setSavingRowId] = useState(null);

  // Bulk upload highlight state
  const [updatedJobIds, setUpdatedJobIds] = useState(new Set());
  const snapshotRef = useRef(null);

  // Snapshot current data before bulk upload (for diff comparison)
  const takeSnapshot = useCallback(() => {
    const map = new Map();
    allData.forEach((row) => {
      if (row.job_id) {
        map.set(row.job_id, {
          local_compensation: row.local_compensation,
          compensation: row.compensation,
          total_positions: row.total_positions,
        });
      }
    });
    snapshotRef.current = map;
  }, [allData]);

  // Fetch data — pass { silent: true } to skip loading spinner and preserve row order (e.g. after inline edit)
  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!email) return;
    if (!silent) setLoading(true);
    try {
      const response = await getCompRequisitionView(email);
      const newData = Array.isArray(response) ? response : [];

      // If snapshot exists, compare and find changed rows
      if (snapshotRef.current) {
        const changedIds = new Set();
        const safeNum = (v) => (v == null || v === '' ? null : Number(v));
        newData.forEach((row) => {
          if (!row.job_id) return;
          const old = snapshotRef.current.get(row.job_id);
          if (!old) return; // new row not in snapshot — skip
          const compChanged = safeNum(old.local_compensation) !== safeNum(row.local_compensation);
          const hqChanged = safeNum(old.compensation) !== safeNum(row.compensation);
          const posChanged = safeNum(old.total_positions) !== safeNum(row.total_positions);
          if (compChanged || hqChanged || posChanged) {
            changedIds.add(row.job_id);
          }
        });
        setUpdatedJobIds(changedIds);
        snapshotRef.current = null; // Clear snapshot after comparison

        // Sort updated rows to top
        newData.sort((a, b) => {
          const aUpdated = changedIds.has(a.job_id) ? 0 : 1;
          const bUpdated = changedIds.has(b.job_id) ? 0 : 1;
          return aUpdated - bUpdated;
        });
        setAllData(newData);
      } else if (silent) {
        // Silent refresh: preserve existing row order by merging fresh data into current positions
        setAllData((prev) => {
          const freshMap = new Map(newData.map((row) => [row.job_id, row]));
          // Update existing rows in-place, preserving order
          const merged = prev.map((row) => freshMap.get(row.job_id) || row);
          // Append any new rows that weren't in the previous list
          const existingIds = new Set(prev.map((r) => r.job_id));
          const added = newData.filter((row) => !existingIds.has(row.job_id));
          return [...merged, ...added];
        });
      } else {
        setAllData(newData);
      }
    } catch {
      SbNotification.error(COMP_MESSAGES.LOAD_FAILED, COMP_MESSAGES.LOAD_FAILED_DESC);
      setAllData([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [email]);

  // Clear highlights
  const clearUpdatedJobIds = useCallback(() => {
    setUpdatedJobIds(new Set());
  }, []);

  // Missing count for warning banner
  const missingCount = useMemo(
    () => allData.filter((r) => r.compensation_source === COMP_SOURCE.MISSING).length,
    [allData],
  );

  // Handle Ant table onChange (filters, pagination, sorter)
  const handleTableChange = useCallback((pagination, filters) => {
    setTableFilters(filters || {});
    // Cancel edit on filter change
    setEditingRowId(null);
    setEditValues({});
  }, []);

  // Edit handlers
  const handleEditStart = useCallback((record) => {
    setEditingRowId(record.job_id);
    setEditValues({
      local_compensation: record.local_compensation ?? null,
      total_positions: record.total_positions ?? 1,
    });
  }, []);

  const handleEditChange = useCallback((field, value) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditingRowId(null);
    setEditValues({});
  }, []);

  const handleEditSave = useCallback(
    async (record) => {
      if (!record?.job_id) return;

      const hasCompensation = editValues.local_compensation != null && editValues.local_compensation !== '';
      const hasPositions = editValues.total_positions != null && editValues.total_positions !== '';

      // ── Client-side validation before API call ──────────────────────────
      // Compensation and position count can each be set independently — only
      // block the save if neither field has a value.
      if (!hasCompensation && !hasPositions) {
        SbNotification.warning(COMP_MESSAGES.ERR_NO_FIELDS, COMP_MESSAGES.ERR_NO_FIELDS_DESC);
        return;
      }
      if (hasCompensation && typeof editValues.local_compensation === 'number' && editValues.local_compensation < 0) {
        SbNotification.error(COMP_MESSAGES.ERR_INVALID_COMP, COMP_MESSAGES.ERR_INVALID_COMP_DESC);
        return;
      }
      if (hasPositions && (!Number.isInteger(editValues.total_positions) || editValues.total_positions < 0)) {
        SbNotification.error(COMP_MESSAGES.ERR_INVALID_POSITIONS, COMP_MESSAGES.ERR_INVALID_POSITIONS_DESC);
        return;
      }

      // Robust Insert vs Update: treat MISSING, null, undefined, empty as "no record exists"
      const isMissing =
        record.compensation_source === COMP_SOURCE.MISSING ||
        !record.compensation_source ||
        record.compensation == null;

      // For an existing record, only send fields that actually changed from what's
      // stored -- handleEditStart pre-fills local_compensation from the record, so
      // resaving without touching it must never resend that value, or the backend
      // treats it as a fresh edit and re-stamps compensation_source to ACTUAL
      // (clobbering an ESTIMATED/BENCHMARK source over an unrelated positions edit).
      const changedFields = {};
      if (!isMissing) {
        if (hasCompensation && editValues.local_compensation !== (record.local_compensation ?? null)) {
          changedFields.local_compensation = editValues.local_compensation;
        }
        if (hasPositions && editValues.total_positions !== (record.total_positions ?? 1)) {
          changedFields.total_positions = editValues.total_positions;
        }
        if (Object.keys(changedFields).length === 0) {
          // Nothing actually changed -- close the row without calling the API.
          setEditingRowId(null);
          setEditValues({});
          return;
        }
      }

      setSavingRowId(record.job_id);

      const insertPayload = {
        job_id: record.job_id,
        total_positions: editValues.total_positions ?? 1,
        update_channel: UPDATE_CHANNEL.ADMIN_UI,
        ...(hasCompensation && {
          local_compensation: editValues.local_compensation,
          compensation_source: COMP_SOURCE.ACTUAL,
        }),
      };

      const updatePayload = {
        update_channel: UPDATE_CHANNEL.ADMIN_UI,
        ...changedFields,
      };

      let result;

      try {
        if (isMissing) {
          result = await insertCompensation(insertPayload);
          SbNotification.success(COMP_MESSAGES.INSERT_SUCCESS);
        } else {
          try {
            result = await updateCompensation(record.job_id, updatePayload);
            SbNotification.success(COMP_MESSAGES.SAVE_SUCCESS);
          } catch (updateError) {
            // Fallback: if Update returns 404 (no record), auto-retry with Insert
            const errCode = updateError?.response?.data?.error_code;
            const status = updateError?.response?.status;
            if (status === 404 || errCode === 7302) {
              result = await insertCompensation(insertPayload);
              SbNotification.success(COMP_MESSAGES.INSERT_SUCCESS);
            } else {
              throw updateError;
            }
          }
        }

        setEditingRowId(null);
        setEditValues({});

        // Silent re-fetch to get correct updated_by name, fx_timestamp, and HQ values
        // without resetting the table loading/scroll state
        fetchData({ silent: true });
      } catch (error) {
        // Try specific error code handler first; fall back to generic
        if (!handleCompErrorCode(error)) {
          const status = error?.response?.status;
          if (status === 409) {
            SbNotification.error(COMP_MESSAGES.ERR_RECORD_EXISTS, COMP_MESSAGES.ERR_RECORD_EXISTS_DESC);
          } else {
            SbNotification.error(COMP_MESSAGES.SAVE_FAILED, COMP_MESSAGES.SAVE_FAILED_DESC);
          }
        }
      } finally {
        setSavingRowId(null);
      }
    },
    [editValues, fetchData],
  );

  return {
    allData,
    loading,
    missingCount,
    tableFilters,
    handleTableChange,
    editingRowId,
    editValues,
    savingRowId,
    handleEditStart,
    handleEditChange,
    handleEditSave,
    handleEditCancel,
    fetchData,
    updatedJobIds,
    takeSnapshot,
    clearUpdatedJobIds,
  };
};

export default useCompensationManagement;
